import { useMemo, useState, useCallback, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import {
  Col,
  Row,
  Card,
  CardBody,
  Button,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap"

import DeleteModal from "components/Common/DeleteModal"

// Custom Hooks
import { useDeleteVehicleStock } from "hooks/stockHook"
import { STOCK_SORT_OPTIONS, useVehicleImporterGroupStocks } from "hooks/useVehicleImporter"

import { useListParams } from "hooks/useListParams"
import ButtonTooltip from "components/Common/ButtonTooltip"
import { downloadVehiclePdf, downloadVehicleStockPdf } from "helpers/pdf_helper"
import { useResponsiveCollapse } from "hooks/useWindowSize"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import ImageViewer from "components/Common/ImageViewer/ImageViewer"
import StockGroupAccordion from "./components/StockGroupAccordion"
import { getStockColumns } from "components/Common/stock-table/stockColumns"
import StockSorting from "./components/StockSorting"
import FullscreenWrapper from "components/Common/FullscreenWrapper"

const ImporterGroupStocks = ({ importerId }) => {
  document.title = "Importer Stocks"
  const navigate = useNavigate()

  const [stock, setStock] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [viewerOpen, setViewerOpen] = useState(false)
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const [selectedImageUrl, setSelectedImageUrl] = useState("")
  const [imageTitle, setImageTitle] = useState("Auction Sheet")
  const [openAccordionItems, setOpenAccordionItems] = useState([])
  const [groupPages, setGroupPages] = useState({}) // Store pagination state for each group

  const openImageViewer = url => {
    setSelectedImageIndex(0)
    setViewerOpen(true)
    setSelectedImageUrl(url || "")
  }

  const { params, resetParams, hasActiveFilters, updateParams } = useListParams(
    {
      group_page: 1,
      group_per_page: 10,
      search: "",
      group_sort: STOCK_SORT_OPTIONS.TITLE,
    }
  )

  const queryParams = useMemo(
    () => ({
      ...params,
    }),
    [params]
  )

  const {
    data: stocksResponse,
    isLoading,
    refetch: stocksRefetch,
    isRefetching,
  } = useVehicleImporterGroupStocks(
    { importerId, ...queryParams },
    Boolean(importerId)
  )

  const { mutateAsync: deleteStock, isPending: stockDeleteLoading } =
    useDeleteVehicleStock()

  // Initialize group pages from API response
  useEffect(() => {
    if (stocksResponse?.data?.groups) {
      const newGroupPages = {}
      Object.keys(stocksResponse.data.groups).forEach(groupName => {
        newGroupPages[groupName] = 1 // Default to page 1 for each group
      })
      setGroupPages(newGroupPages)
    }
  }, [stocksResponse])

  const handleAddNewClick = () => {
    navigate("/stock-add-edit?importerId=" + importerId)
  }

  const handleStockClick = useCallback(stockData => {
    if (stockData?.stock_id) {
      navigate(`/stock-add-edit/${stockData?.stock_id}`)
    }
  }, [])

  const onClickDelete = useCallback(stockData => {
    setStock(stockData)
    setDeleteModal(true)
  }, [])

  const handleDeleteStock = useCallback(async () => {
    if (stock?.id) {
      try {
        const res = await deleteStock(stock.id)
        toast.success(res?.message ?? "Stock deleted successfully.")
        setDeleteModal(false)
        stocksRefetch() // Refresh the list after deletion
      } catch (error) {
        console.error("Failed to delete stock:", error)
        toast.error("Failed to delete stock.")
      }
    }
  }, [stock, deleteStock, stocksRefetch])

  const handleListRefresh = () => {
    stocksRefetch()
  }

  const onClickDownload = async stockId => {
    try {
      await downloadVehiclePdf(stockId)
    } catch (error) {}
  }

  const { isMobile, toggleRow, isExpanded } = useResponsiveCollapse()

  const columns = useMemo(
    () =>
      getStockColumns({
        navigate,
        isMobile,
        toggleRow,
        isExpanded,
        handleStockClick,
        onClickDelete,
        onClickDownload,
        openImageViewer,
        setImageTitle,
      }),
    [navigate, isMobile, toggleRow, isExpanded]
  )

  // Extract grouped data and meta info
  const groupsData = stocksResponse?.data?.groups || {}
  const metaInfo = stocksResponse?.data?.meta || {}

  const handleStockDownload = async () => {
    try {
      await downloadVehicleStockPdf(queryParams)
    } catch (error) {}
  }

  const toggleAccordion = groupName => {
    setOpenAccordionItems(prev =>
      prev.includes(groupName)
        ? prev.filter(item => item !== groupName)
        : [...prev, groupName]
    )
  }

  // Handle parent pagination (groups)
  const handlePageChange = page => {
    updateParams(prev => ({
      ...prev,
      group_page: Number(page),
    }))
  }

  // Handle group-specific pagination (vehicles within a group)
  const handleGroupPageChange = (groupName, page) => {
    setGroupPages(prev => ({
      ...prev,
      [groupName]: page,
    }))
  }

  // Function to toggle all accordions open/close
  const handleToggleAllAccordions = () => {
    const allGroupNames = Object.keys(groupsData)
    if (openAccordionItems.length === allGroupNames.length) {
      // All are open → close all
      setOpenAccordionItems([])
    } else {
      // Open all
      setOpenAccordionItems(allGroupNames)
    }
  }

  // Auto-open first accordion on load
  useEffect(() => {
    if (Object.keys(groupsData).length > 0 && openAccordionItems.length === 0) {
      const firstGroup = Object.keys(groupsData)[0]
      setOpenAccordionItems([firstGroup])
    }
  }, [groupsData])

  // Render parent pagination for groups
  const renderParentPagination = () => {
    if (!metaInfo || metaInfo.last_page <= 1) return null

    const currentPage = metaInfo.current_page || 1
    const totalPages = metaInfo.last_page || 1

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="text-muted">
          Showing groups {(currentPage - 1) * metaInfo.per_page + 1} to{" "}
          {Math.min(currentPage * metaInfo.per_page, metaInfo.total_groups)} of{" "}
          {metaInfo.total_groups}
        </div>
        <Pagination className="pagination pagination-rounded">
          <PaginationItem disabled={currentPage === 1}>
            <PaginationLink
              previous
              onClick={() => handlePageChange(currentPage - 1)}
            />
          </PaginationItem>

          {[...Array(totalPages)].map((_, i) => {
            const page = i + 1
            // Show first, last, and pages around current page
            if (
              page === 1 ||
              page === totalPages ||
              (page >= currentPage - 1 && page <= currentPage + 1)
            ) {
              return (
                <PaginationItem key={page} active={currentPage === page}>
                  <PaginationLink onClick={() => handlePageChange(page)}>
                    {page}
                  </PaginationLink>
                </PaginationItem>
              )
            } else if (page === currentPage - 2 || page === currentPage + 2) {
              return (
                <PaginationItem key={page}>
                  <PaginationLink disabled>...</PaginationLink>
                </PaginationItem>
              )
            }
            return null
          })}

          <PaginationItem disabled={currentPage === totalPages}>
            <PaginationLink
              next
              onClick={() => handlePageChange(currentPage + 1)}
            />
          </PaginationItem>
        </Pagination>
      </div>
    )
  }

  // Render pagination for vehicles within a group
  const renderGroupPagination = (groupName, vehicles) => {
    if (!Array.isArray(vehicles) || vehicles.length <= 20) return null

    const currentPage = groupPages[groupName] || 1
    const totalPages = Math.ceil(vehicles.length / 20)

    if (totalPages <= 1) return null

    return (
      <Pagination className="pagination pagination-rounded justify-content-center mt-3">
        <PaginationItem disabled={currentPage === 1}>
          <PaginationLink
            previous
            onClick={() => handleGroupPageChange(groupName, currentPage - 1)}
          />
        </PaginationItem>

        {[...Array(totalPages)].map((_, i) => {
          const page = i + 1
          // Show first, last, and pages around current page
          if (
            page === 1 ||
            page === totalPages ||
            (page >= currentPage - 1 && page <= currentPage + 1)
          ) {
            return (
              <PaginationItem key={page} active={currentPage === page}>
                <PaginationLink
                  onClick={() => handleGroupPageChange(groupName, page)}
                >
                  {page}
                </PaginationLink>
              </PaginationItem>
            )
          } else if (page === currentPage - 2 || page === currentPage + 2) {
            return (
              <PaginationItem key={page}>
                <PaginationLink disabled>...</PaginationLink>
              </PaginationItem>
            )
          }
          return null
        })}

        <PaginationItem disabled={currentPage === totalPages}>
          <PaginationLink
            next
            onClick={() => handleGroupPageChange(groupName, currentPage + 1)}
          />
        </PaginationItem>
      </Pagination>
    )
  }

  // Get paginated vehicles for a group
  const getPaginatedGroupData = (groupName, vehicles) => {
    if (!Array.isArray(vehicles)) return []

    const currentPage = groupPages[groupName] || 1
    const itemsPerPage = 20
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage

    return vehicles.slice(startIndex, endIndex)
  }

  return (
    <FullscreenWrapper>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteStock}
        loading={stockDeleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />

      <ImageViewer
        images={[selectedImageUrl]}
        initialIndex={selectedImageIndex}
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        title={imageTitle}
      />
      <Row>
        <Col lg="12">
          <Card>
            <CardBody className="border-bottom ">
              <Row className="justify-content-between align-items-center">
                <Col xs={12} sm={6}>
                  <h5 className="mb-0">
                    Importer Stocks{" "}
                    <span className="badge badge-soft-info">
                      {metaInfo.total_vehicles || 0} stocks in{" "}
                      {metaInfo.total_groups || 0} groups
                    </span>
                  </h5>
                </Col>
                <Col xs={12} sm={6}>
                  <div className="text-sm-end mt-2 mt-sm-0 me-5">
                    <Button
                      color="primary"
                      onClick={handleAddNewClick}
                      className="me-1"
                    >
                      Add New Stock
                    </Button>

                    <ButtonTooltip
                      type="refresh"
                      btnId="refreshStockList"
                      onClick={handleListRefresh}
                      loading={isRefetching}
                      color="light"
                      className="btn btn-light me-1"
                    />
                    {hasActiveFilters && (
                      <Button
                        className="ms-1"
                        color="danger"
                        onClick={resetParams}
                      >
                        Reset Filters
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>
            </CardBody>
            <CardBody>
              <Row>
                <Col sm={6} lg={6} className="mb-3">
                  <GlobalInputSearch
                    value={params.search}
                    onChange={val =>
                      updateParams(prev => ({
                        ...prev,
                        search: val,
                        group_page: 1,
                      }))
                    }
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                    <StockSorting />
                    <Button
                      color="secondary"
                      className="ms-1"
                      size="sm"
                      onClick={handleToggleAllAccordions}
                    >
                      {openAccordionItems.length ===
                      Object.keys(groupsData).length
                        ? "Collapse All"
                        : "Expand All"}
                    </Button>
                  </div>
                </Col>
              </Row>

              {/* Grouped Stocks Display */}
              {isLoading || isRefetching ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading stocks...</p>
                </div>
              ) : Object.keys(groupsData).length === 0 ? (
                <div className="text-center py-5">
                  <p className="text-muted">
                    No stocks found for this importer
                  </p>
                </div>
              ) : (
                <>
                  <StockGroupAccordion
                    groupsData={groupsData}
                    openAccordionItems={openAccordionItems}
                    toggleAccordion={toggleAccordion}
                    groupPages={groupPages}
                    renderGroupPagination={renderGroupPagination}
                    getPaginatedGroupData={getPaginatedGroupData}
                    columnsProps={columns}
                  />

                  {/* Parent Pagination for Groups */}
                  {renderParentPagination()}
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </FullscreenWrapper>
  )
}

export default ImporterGroupStocks
